import type {
  ParcelDto,
  PublicParcelDto,
} from "@babackapp/private-api-client/types";
import {
  getTrackingStatusTranslation,
  TrackingStatus,
} from "@babackapp/tracking-status-registry";

type Step = {
  id: string;
  name: string;
  texts?: { [key: string]: string };
  matches: string[];
  text: string;
};

type UseStepsParams = {
  locale: string;
  parcel?: ParcelDto | PublicParcelDto | null;
  filterStepId?: string;
};

function getStatusTitle(status: TrackingStatus, locale: string): string {
  return getTrackingStatusTranslation(status, locale).title;
}

export function useSteps({
  locale,
  parcel,
  filterStepId,
}: UseStepsParams): Step[] {
  const allSteps: Step[] = [
    {
      id: "pre-transit",
      name: getStatusTitle(TrackingStatus.PRE_TRANSIT, locale),
      matches: ["PRE_TRANSIT"],
      text: getStatusTitle(TrackingStatus.PRE_TRANSIT, locale),
    },
    {
      id: "in-transit",
      name: getStatusTitle(TrackingStatus.IN_TRANSIT, locale),
      matches: ["IN_TRANSIT", "REDIRECT_TO_PICKUP"],
      text: getStatusTitle(TrackingStatus.IN_TRANSIT, locale),
      texts: {
        IN_TRANSIT: getStatusTitle(TrackingStatus.IN_TRANSIT, locale),
        REDIRECT_TO_PICKUP: getStatusTitle(
          TrackingStatus.REDIRECT_TO_PICKUP,
          locale,
        ),
      },
    },
    {
      id: "available-for-pickup",
      name: getStatusTitle(TrackingStatus.AVAILABLE_FOR_PICKUP, locale),
      matches: [
        "AVAILABLE_FOR_PICKUP",
        "WAITING_AT_PICKUP_POINT",
        "WAITING_AT_PICKUP_POINT_SEVERE",
      ],
      text: getStatusTitle(TrackingStatus.AVAILABLE_FOR_PICKUP, locale),
    },
    {
      id: "delivered",
      name: getStatusTitle(TrackingStatus.DELIVERED, locale),
      texts: {
        DELIVERED: getStatusTitle(TrackingStatus.DELIVERED, locale),
        COLLECTED_AT_PICKUP: getStatusTitle(
          TrackingStatus.COLLECTED_AT_PICKUP,
          locale,
        ),
        DELIVERED_TO_NEIGHBOUR: getStatusTitle(
          TrackingStatus.DELIVERED_TO_NEIGHBOUR,
          locale,
        ),
      },
      matches: ["DELIVERED", "COLLECTED_AT_PICKUP", "DELIVERED_TO_NEIGHBOUR"],
      text: getStatusTitle(TrackingStatus.DELIVERED, locale),
    },
  ];

  if (!parcel || !parcel.events) {
    return allSteps;
  }

  const eventStatuses = parcel.events.map((event) => event.status);

  const filteredSteps = allSteps.filter((step) => {
    if (step.id === "available-for-pickup") {
      return step.matches.some((match) => eventStatuses.includes(match as any));
    }
    return true;
  });

  if (filterStepId) {
    return filteredSteps.filter((step) => step.id === filterStepId);
  }

  return filteredSteps;
}
